import {
  Component,
  computed,
  inject,
  OnInit,
  signal,
  ViewChild,
} from "@angular/core";
import { MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { SelectorOptionsComponent } from "../../../../../features/selector-options/selector-options.providers";
import { SortDir } from "../../../../../features/shared/data/sort-dir.enum";
import { AggregatedFilterComponent } from "../../../../../shared/components/filtering/aggregated-filter/aggregated-filter.component";
import { FiltersRowComponent } from "../../../../../shared/components/filtering/filters-row/filters-row.component";
import { SelectorOptionsService } from "../../../../../shared/components/filtering/selector-options.service";
import {
  SortingOption,
  SortingSelectorComponent,
} from "../../../../../shared/components/filtering/sorting-selector/sorting-selector.component";
import { ReloadComponent } from "../../../../../shared/components/reload/reload.component";
import { ContentViewTypeSelectorComponent } from "../../../../../shared/content/components/content-type-selector/content-view-type-selector.component";
import { BrandService } from "../../../../../shared/services/api/brand.service";
import { TrackingService } from "../../../../../shared/analytics/tracking.service";
import { BrandUrl } from "../../../../brand.url";
import { BrandCampaignService } from "../../../shared/services/brand-campaign.service";
import { BetaCampaignTrackingEvents } from "../../../shared/tracking/beta-campaign.tracking-events";
import { BrandPublicationCalendarComponent } from "../brand-publication-calendar/brand-publication-calendar.component";
import { BrandPublicationListComponent } from "../brand-publication-list/brand-publication-list.component";
import {
  FilterOutput,
  FilterType,
} from "../../../../../shared/components/filtering/filter.interfaces";
import { BrandPublicationTilesComponent } from "../brand-publication-tiles/brand-publication-tiles.component";
import { ContentViewType } from "../../../../../shared/content/content-view-type.enum";

@Component({
  standalone: true,
  selector: "app-brand-content",
  templateUrl: "./brand-content.component.html",
  styleUrls: ["./brand-content.component.scss"],
  imports: [
    BrandPublicationListComponent,
    MatIcon,
    RouterLink,
    SortingSelectorComponent,
    FiltersRowComponent,
    AggregatedFilterComponent,
    BrandPublicationTilesComponent,
    TranslateModule,
    MatIconButton,
    ContentViewTypeSelectorComponent,
    ReloadComponent,
    BrandPublicationCalendarComponent,
  ],
})
export class BrandContentComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  protected readonly ContentViewType = ContentViewType;
  protected readonly FilterType = FilterType;
  protected readonly BrandUrl = BrandUrl;
  protected readonly brandId = inject(BrandService).currentBrandId;
  protected readonly campaign =
    inject(BrandCampaignService).currentBrandCampaign;
  protected readonly getTypesInteractor =
    SelectorOptionsComponent.provideGetOptionsInteractorByType(
      FilterType.PublicationStatus,
      inject(SelectorOptionsService),
    );
  protected readonly filters: FilterType[] = [
    FilterType.Publisher,
    FilterType.PublicationTemplateType,
    FilterType.TimePeriod,
  ];
  protected readonly sortingOptions: SortingOption[] = [
    {
      name: "newestFirst",
      field: "CREATED_AT",
      dir: SortDir.Desc,
    },
    {
      name: "oldestFirst",
      field: "CREATED_AT",
      dir: SortDir.Asc,
    },
  ];
  protected readonly isThereNoContent = signal(false);

  protected readonly fixedFilters: FilterOutput[] = [
    {
      type: FilterType.Campaign,
      value: {
        id: inject(BrandCampaignService).currentBrandCampaignId,
        name: "campaign",
      },
    },
    {
      type: FilterType.Creator,
      value: {
        id: inject(BrandService).currentBrandId,
        name: "creator",
      },
    },
  ];

  protected readonly aggregatedFilters = signal<FilterOutput[]>([]);
  protected readonly statusFilter = signal<FilterOutput | undefined>(undefined);
  protected readonly sortBy = signal<SortingOption>(this.sortingOptions[0]);
  protected readonly viewType = signal<ContentViewType>(ContentViewType.Grid);
  protected readonly isInitialized = signal(false);
  protected readonly activeFilters = computed(() => [
    ...this.aggregatedFilters(),
    ...(this.statusFilter()?.value ? [this.statusFilter()] : []),
    ...this.fixedFilters,
  ]);
  protected readonly areFiltersPristine = computed(
    () => this.aggregatedFilters().length === 0 && !this.statusFilter(),
  );

  @ViewChild(AggregatedFilterComponent)
  private aggregatedFiltersComponent!: AggregatedFilterComponent;
  @ViewChild(FiltersRowComponent)
  private filtersComponent!: FiltersRowComponent;
  @ViewChild(SortingSelectorComponent)
  private sortingComponent!: SortingSelectorComponent;

  private readonly betaCampaignEvents: BetaCampaignTrackingEvents;

  constructor(trackingService: TrackingService) {
    this.betaCampaignEvents = new BetaCampaignTrackingEvents(
      this.brandId,
      trackingService,
    );
  }

  public ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.viewType.set(params.view ?? ContentViewType.Grid);
    });
  }

  protected resetFilters(): void {
    this.aggregatedFiltersComponent.reset();
    this.filtersComponent.reset();
    this.sortingComponent.reset();
    this.isThereNoContent.set(false);
  }

  protected gotToContentCreation(): void {
    this.betaCampaignEvents.publicationTemplateCreated();
    this.router.navigate([
      BrandUrl.Campaign.PublicationTemplate(this.brandId, this.campaign.id)
        .Selection,
    ]);
  }

  protected onAggregatedFiltersChanged(filters: FilterOutput[]): void {
    this.isInitialized.set(true);
    this.aggregatedFilters.set(filters);
  }
}
